import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import Button from '../Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from "@material-ui/core/Checkbox";
import CloseIcon from '@material-ui/icons/Close';
import TextField from '@material-ui/core/TextField';
import {
  getRateCodes,
} from '../../actions/members';

const useStyles = makeStyles({
  root: {
    width: '400px',
  },
  adjustmentForm: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    padding: '10px 0px 20px 0px',
    margin: '0px 10px',
    transition: 'all 0.2s ease',
  },
  createVolumeContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    border: 'none',
  },
  textAreaContainer: {
    margin: '10px',
    height: '100px',
  },
  textArea: {
    fontFamily: 'Roboto Condensed',
    fontSize: '20px',
    overflowY: 'scroll',
    height: '100%',
    width: '100%',
    resize: 'none',
    '&:focus': {
      outline: 'none !important',
    },
  },
  menuBar: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '18px',
    fontWeight: 'bold',
    padding: '10px',
    backgroundColor: '#202124',
    color: 'white',
    letterSpacing: '1px',
    '& .MuiSvgIcon-root': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#585858',
        color: 'white',
      },
    },
  },
  optionContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '0px 10px',
    transition: 'all 0.2s ease',
    marginBottom: '10px',
  },
  disableButton: {
    cursor: 'default',
    color: 'rgba(0,0,0,0.2)',
  },
  enableButton: {
    transition: 'all 0.3s linear',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '15px',
    fontWeight: 'bold',
    margin: '10px',
    paddingBottom: '3px',
    borderBottom: '1px solid black',
  },
  contentFullView: {
    fontSize: '15px',
    maxHeight: '100%',
    padding: '10px',
    whiteSpace: 'pre-wrap',
  },
  confirmDialog: {
    textAlign: 'center',
    '& .MuiTypography-root': {
      width: '100%',
    },
  },
  errorMessage: {
    color: 'red',
    margin: '10px',
    marginTop: '0px',
  }
});

const getLast12Months = () => {
  const months = [];
  const now = new Date();
  now.setMonth(now.getMonth() - 1);
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    months.push(`${year}-${month}`);
  }
  return months;
};

const MemberFix = props => {
  const {
    closePopup,
    handleChange,
    member,
    meter,
    doFetchGetRateCodes
  } = props;
  const classes = useStyles();
  const [volume, setVolume] = useState(0);
  const [rateCode, setRateCode] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [error, setError] = useState(null);
  const [monthlyUsage, setMonthlyUsage] = useState(false);
  const [inputs, setInputs] = useState({});
  const months = getLast12Months().slice();
  const leftColumn = months.slice(0, 6);
  const rightColumn = months.slice(6);

  const submitForm = () => {
    setOpenPopup(true);
  };

  const handleClose = () => {
    setOpenPopup(false);
  };

  const handleConfirm = () => {
    let update = {
      annual_kwh: monthlyUsage ? Object.values(inputs).reduce((acc, val) => acc + val, 0) : volume,
      utility_rate_code: rateCode,
      status: 'new',
      // GX-6990: this screen always writes usage, so it is always admin-sourced
      usage_source: 'admin_portal'
    }
    if (monthlyUsage) {
      update['usages'] = inputs;
    }
    handleChange(update);
    setOpenPopup(false);
    closePopup(false);
    setVolume(0);
    setRateCode(null);
  };

  const handleVolumeChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setVolume(value);
    }
  };

  const handleInputChange = (index, value) => {
    setInputs((prev) => ({
      ...prev,
      [index]: Number(value)
    }));
  };

  useEffect(() => {
    if (meter) {
      setVolume(meter.get('annual_kwh') || 0)
      doFetchGetRateCodes(meter.get('tdsp_duns'))
      // NOTE: utility_rate_code carries two value domains by design (GX-3698):
      // this flow writes TDSP rate codes, the contract review pop-up writes
      // rate-class labels (Residential / Small Commercial / Large Commercial) —
      // a meter reviewed there will show blank here until re-selected
      setRateCode(meter.get('utility_rate_code'))
    }
  }, [meter])

  return (
    <div className={classes.root}>
      <Card className={classes.createVolumeContainer} variant='outlined'>
        <div className={`${classes.menuBar}`}>
          <span>Fix Reject</span>
          <CloseIcon onClick={() => closePopup()} />
        </div>
        <div className={classes.adjustmentForm} style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
          <Grid item sm={12}>
            <FormControl variant='outlined' size='small' fullWidth>
              <label id='rate-code'>Rate Code</label>
              <Select onChange={(e) => setRateCode(e.target.value)} value={rateCode}>
                {member['rateCodes'].map(val => <MenuItem key={val['tdsp_rate_codes']} value={val['tdsp_rate_codes']}>{val['tdsp_rate_codes']}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item sm={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={monthlyUsage}
                  onChange={(e) => setMonthlyUsage(e.target.checked)}
                  name="checkMonthlyUsage"
                />
              }
              label={`Enter monthly usage`}
              className={`${classes.toggleContainer}`}
            />
          </Grid>
          {
            monthlyUsage ? (
              <Grid item sm={12} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <FormControl
                  variant='outlined'
                  size='small'
                  fullWidth
                  style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                    {leftColumn.map((month) => (
                      <div key={month} style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', minWidth: '50px' }}>{month}</label>
                        <TextField
                          type="number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={inputs[month] || ''}
                          onChange={(e) => handleInputChange(month, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                    {rightColumn.map((month) => (
                      <div key={month} style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', minWidth: '50px' }}>{month}</label>
                        <TextField
                          type="number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={inputs[month] || ''}
                          onChange={(e) => handleInputChange(month, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </FormControl>
                <label>Total monthly usage: {Object.values(inputs).reduce((acc, val) => acc + Number(val || 0), 0)}</label>
              </Grid>
            ) : (
              <Grid item sm={12}>
                <div>
                  <label id='rate-code'>Annual Volume</label>
                  <TextField
                    onChange={handleVolumeChange}
                    value={volume}
                    variant='outlined'
                    size='small'
                    style={{ width: '100%' }}
                  />
                </div>
              </Grid>
            )
          }
        </div>
        {error && <div className={classes.errorMessage}>{error}</div>}
        <div className={classes.optionContainer}>
          <Button
            className={classes.enableButton}
            color='secondary'
            varian='raised'
            onClick={() => {
              setRateCode(null);
              setInputs({});
              setMonthlyUsage(false);
              closePopup(false);
            }}
          >
            Cancel
          </Button>
          <Button
            className={classes.enableButton}
            color='primary'
            varian='raised'
            onClick={async () => {
              setError('');
              const allUsageFilled = monthlyUsage
                ? getLast12Months().every(m => inputs[m] !== undefined && inputs[m] !== '' && inputs[m] > 0)
                : false;

              const isValid = monthlyUsage
                ? allUsageFilled && !!rateCode
                : volume > 0 && !!rateCode;

              if (isValid) {
                await submitForm();
              } else {
                const errorMsg = monthlyUsage
                  ? 'Please enter valid monthly usage and select a rate code'
                  : 'Please add a volume and rate code to proceed';
                setError(errorMsg);
              }
            }}
          >
            Save
          </Button>
        </div>
      </Card>
      <Dialog
        onClose={handleClose}
        aria-labelledby='customized-dialog-title'
        open={openPopup}
      >
        <DialogTitle
          className={classes.confirmDialog}
          id='customized-dialog-title'
          onClose={handleClose}
        >
          <span>Please Confirm</span>
        </DialogTitle>
        <DialogContent dividers>
          Are you sure you want to fix this reject?
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => handleClose()} color='secondary'>
            Close
          </Button>
          <Button
            className={classes.enableButton}
            autoFocus
            onClick={handleConfirm}
            color='primary'
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = (state) => ({
  member: state.selectedMember.toJSON(),
});

const mapDispatchToProps = (dispatch) => ({
  doFetchGetRateCodes: dunsNumber => {
    dispatch(getRateCodes(dunsNumber));
  },
  dispatch,
});

const ConnectedMemberFix = connect(
  mapStateToProps,
  mapDispatchToProps
)(MemberFix);

export default ConnectedMemberFix;
